import * as B from '@blueprintjs/core';
import * as Sentry from '@sentry/react';
import * as I from 'immutable';
import moment from 'moment';
import React from 'react';
import {StringParam, useQueryParams} from 'use-query-params';

import CenteredSpinner from 'app/components/CenteredSpinner';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {
  ApiImageryContract,
  ApiImageryContractWithProjects,
  ApiImageryPurchaseBillingRecord,
} from 'app/modules/Remote/Project';
import {
  useAccessSubscription,
  useCurrentImageryContract,
  useImageryContracts,
  useImageryPurchaseBillingRecords,
  useOrganizationImageryPurchaseBillingRecords,
} from 'app/providers/ImageryContractProvider';
import {useProjects} from 'app/providers/ProjectsProvider';
import colors from 'app/styles/colors.json';
import {findPrimaryFeatureCollection} from 'app/utils/featureCollectionUtils';
import {useFeatures} from 'app/utils/featureUtils';
import {StatusMaybe, useTimestampMs} from 'app/utils/hookUtils';
import {paysAtCheckout} from 'app/utils/organizationUtils';
import {makeUuidPrefix} from 'app/utils/routeUtils';
import {isOwnerOrAdmin} from 'app/utils/userUtils';

import cs from './BillingView.module.scss';
import CardPurchasesTable from './ContractHistory/CardPurchasesTable';
import {ContractHistory, PlanBillingCard} from './ContractHistory/ContractHistory';
import ImageryContractTable from './ContractHistory/ImageryContractTable';
import ImageryPrepaidSection from './ContractHistory/ImageryPrepaidSection';
import PortfolioPurchasesTable from './ContractHistory/PortfolioPurchasesTable';
import PortfolioSelect from './ContractHistory/PortfolioSelect';
import {
  Portfolio,
  exportPortfolioOrderHistoryCsv,
  portfoliosWithPurchases,
} from './ContractHistory/utils';
import * as GlobalSettings from './GlobalSettings';

// Small utility function for returning an Immutable Map of features, indexed by feature id.
function makeFeatureById(
  features: I.ImmutableListOf<ApiFeature>
): I.Map<number, I.ImmutableOf<ApiFeature>> {
  return features.reduce(
    (map, f) => map!.set(f!.get('id'), f!),
    I.Map<number, I.ImmutableOf<ApiFeature>>()
  );
}

const today = moment();
const hasEnded = (end: string | null) => end && moment(end).isBefore(today);

type CheckoutTab = 'purchases' | 'receipts' | 'contracts';

const BillingView: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    organizationUsers: ApiOrganizationUser[] | null;
    profile: I.ImmutableOf<ApiOrganizationUser>;
  }>
> = ({organization, organizationUsers, profile}) => {
  const organizationId = organization.get('id');
  const orgPaysAtCheckout = paysAtCheckout(organization);

  const [projects, featureCollectionsById] = useProjects();
  // A checkout org's tab and open portfolio are in the URL. An invoiced org's open portfolio isn't.
  const [{billingTab: tabParam, billingPortfolio: portfolioParam}, setBillingParams] =
    useQueryParams({billingTab: StringParam, billingPortfolio: StringParam});
  const [invoicedProject, setInvoicedProject] = React.useState<Portfolio | null>(null);
  const [projectSelectOpen, setProjectSelectOpen] = React.useState(false);

  const [getFeatures] = useFeatures();
  const [getImageryContracts, {invalidate: invalidateContracts}] = useImageryContracts();
  const [getCurrentImageryContract] = useCurrentImageryContract();
  const [getImageryPurchaseBillingRecords] = useImageryPurchaseBillingRecords();
  const [componentLoadTimestampMs] = useTimestampMs();
  const [getAccessSubscription, cancelSubscription] = useAccessSubscription();

  const organizationRecordsQuery = useOrganizationImageryPurchaseBillingRecords(organizationId, {
    enabled: orgPaysAtCheckout,
  });

  // API response for all contracts in the organization.
  const contractsMaybe = React.useMemo(
    () => getImageryContracts(organizationId),
    [getImageryContracts, organizationId]
  );

  //API response for the organization's access subscription.
  const accessSubscriptionMaybe = React.useMemo(
    () => getAccessSubscription(organizationId),
    [getAccessSubscription, organizationId]
  );

  const currentLensTier = organization.getIn(['tiers', 'Lens']);

  // Associate project ids with names, for our dropdown
  const projectList: {id: string; name: string}[] = React.useMemo(
    () =>
      (projects
        ?.map((project) => ({id: project?.get('id'), name: project?.get('name') || ''}))
        .toArray()
        .filter(({id}) => !!id) as {id: string; name: string}[]) || [],
    [projects]
  );

  // Only owners and admins can check out, so only they see the checkouts' receipts.
  const showReceipts = isOwnerOrAdmin(profile.get('role'));
  // An org that switched to paying at checkout keeps its ended contracts. A failed load still shows
  // the tab, so its error and retry can be seen.
  const showPastContracts =
    contractsMaybe.status === 'error' ||
    (contractsMaybe.status === 'some' && contractsMaybe.value.length > 0);
  const checkoutTabs: {id: CheckoutTab; label: string}[] = [
    {id: 'purchases', label: 'Purchases'},
    ...(showReceipts ? [{id: 'receipts' as const, label: 'Receipts'}] : []),
    ...(showPastContracts ? [{id: 'contracts' as const, label: 'Past contracts'}] : []),
  ];
  const checkoutTab = checkoutTabs.find((tab) => tab.id === tabParam)?.id ?? 'purchases';

  const purchasedPortfolios = React.useMemo(
    () => portfoliosWithPurchases(projectList, organizationRecordsQuery.data ?? []),
    [projectList, organizationRecordsQuery.data]
  );
  const checkoutProject =
    checkoutTab === 'purchases' && portfolioParam
      ? (purchasedPortfolios.find((p) => makeUuidPrefix(p.id) === portfolioParam) ?? null)
      : null;
  const selectedProject = orgPaysAtCheckout ? checkoutProject : invoicedProject;

  const showCheckoutTab = (tab: CheckoutTab) =>
    setBillingParams({billingTab: tab, billingPortfolio: undefined}, 'replaceIn');
  const openCheckoutPortfolio = (portfolio: Portfolio | null) =>
    setBillingParams(
      {billingPortfolio: portfolio ? makeUuidPrefix(portfolio.id) : undefined},
      'replaceIn'
    );

  const featuresById: I.Map<number, I.ImmutableOf<ApiFeature>> | null = React.useMemo(() => {
    // Case: no project has been selected yet, so there aren't any features we can get.
    if (!selectedProject) {
      return null;
    }
    const project = projects?.find((p) => p?.get('id') === selectedProject.id);
    // Purely for type narrowing, since selectedProject is generated from projects
    if (!project) {
      return null;
    }
    const primaryFeatureCollection = findPrimaryFeatureCollection(project, featureCollectionsById);
    const maybeFeatures = getFeatures(primaryFeatureCollection!.get('id'));
    if (maybeFeatures.status === 'some') {
      return makeFeatureById(maybeFeatures.value);
    } else {
      return null;
    }
  }, [selectedProject, projects, featureCollectionsById, getFeatures]);

  // Get all imagery contracts. This is required for the downstream table, but also to
  // check if this org has an imagery contract and should let the user select projects.
  const [allImageryContracts, hasContracts, allImagerySubscriptionIds] = React.useMemo(() => {
    const allActiveContracts =
      contractsMaybe.status === 'some'
        ? contractsMaybe.value.filter((c) => {
            return !hasEnded(c.endDate);
          })
        : null;

    const hasContracts = allActiveContracts && allActiveContracts.length > 0;

    const allImagerySubscriptionIds = allActiveContracts
      // we don't want to fetch invoices for test mode contracts, because the customers
      // will not receive invoices for those
      ?.filter((c) => !c.stripeTest)
      .map((c) => c.stripeSubscriptionId);

    return [allActiveContracts, hasContracts, allImagerySubscriptionIds];
  }, [contractsMaybe.status, contractsMaybe.value]);

  // Once we have a selected project we can fetch the contract for it, and then fetch
  // imagery purchases, which we filter by project because the table expects this. An org that pays
  // at checkout bills no contract, so its purchases are read through the org instead.
  const [currentImageryContract, imageryPurchaseBillingRecords] = React.useMemo((): [
    StatusMaybe<ApiImageryContract | null> | null,
    ApiImageryPurchaseBillingRecord[] | null,
  ] => {
    if (!selectedProject) {
      return [null, null];
    }

    if (orgPaysAtCheckout) {
      return [
        null,
        organizationRecordsQuery.data?.filter((r) => r.projectId === selectedProject.id) ?? null,
      ];
    }

    const opts = {afterMs: componentLoadTimestampMs, allowStaleMs: true};
    const currentContract = getCurrentImageryContract(selectedProject.id, opts);
    const contractRecords =
      currentContract.status === 'some' && currentContract.value != null
        ? getImageryPurchaseBillingRecords(currentContract.value.id, opts)
        : null;
    const imageryPurchases =
      contractRecords?.status === 'some'
        ? contractRecords.value.toJS().filter((ip) => ip.projectId === selectedProject.id)
        : null;

    return [currentContract, imageryPurchases];
  }, [
    componentLoadTimestampMs,
    getCurrentImageryContract,
    getImageryPurchaseBillingRecords,
    organizationRecordsQuery.data,
    orgPaysAtCheckout,
    selectedProject,
  ]);

  // Dont allow user to select a portfolio if:
  // 1. there are no portfolios to select,
  // 2. or we don't know if they have imagery contracts or dont have one
  const projectSelectionDisabled = projectList.length < 1 || !hasContracts;

  // Checkouts draw on imagery_prepaids only for orgs that pay at checkout. Everyone else's prepaid
  // is still in Stripe, shown by ImageryPaymentSection.
  const showImageryPrepaid = orgPaysAtCheckout;

  const [isDownloadingPortfolio, setIsDownloadingPortfolio] = React.useState(false);
  const downloadPortfolio = async (portfolio: {id: string; name: string}) => {
    setIsDownloadingPortfolio(true);
    try {
      await exportPortfolioOrderHistoryCsv({
        portfolio,
        billingRecords: organizationRecordsQuery.data ?? [],
        contracts: contractsMaybe.value ?? [],
        portfolios: projectList,
        organizationId,
      });
    } catch (error) {
      Sentry.captureException(error);
      window.alert('We weren’t able to download your CSV right now. Please try again.');
    } finally {
      setIsDownloadingPortfolio(false);
    }
  };

  const contractHistory = (
    <ContractHistory
      contracts={contractsMaybe}
      reloadContracts={() => invalidateContracts([organizationId, ''], true)}
      organizationId={organizationId}
    />
  );

  // The same dropdown heads both the per-portfolio totals and an open portfolio.
  const portfolioSelect = (
    <PortfolioSelect
      portfolios={purchasedPortfolios}
      selected={selectedProject}
      onSelect={openCheckoutPortfolio}
    />
  );

  const renderCheckoutView = () => {
    if (checkoutTab === 'receipts') {
      return <CardPurchasesTable organizationId={organizationId} />;
    }
    if (checkoutTab === 'contracts') {
      return contractHistory;
    }
    if (selectedProject) {
      return (
        <ContractTable
          // The table reads which columns to hide from the records it first renders.
          key={selectedProject.id}
          organizationId={organizationId}
          currentImageryContract={null}
          allImageryContracts={contractsMaybe.value ?? []}
          billingRecords={imageryPurchaseBillingRecords}
          featuresById={featuresById}
          heading={portfolioSelect}
          actions={
            <B.Button
              small
              minimal
              title={`Download CSV of ${selectedProject.name} purchases`}
              aria-label={`Download CSV of ${selectedProject.name} purchases`}
              icon={<B.Icon icon="download" color={colors.darkestGray} size={14} />}
              loading={isDownloadingPortfolio}
              disabled={!imageryPurchaseBillingRecords?.length}
              onClick={() => downloadPortfolio(selectedProject)}
            />
          }
        />
      );
    }
    if (organizationRecordsQuery.status === 'error') {
      return (
        <B.NonIdealState
          icon="warning-sign"
          description="We weren’t able to load your purchases right now."
          action={
            <B.Button
              text="Try Again"
              intent="primary"
              onClick={() => organizationRecordsQuery.refetch()}
            />
          }
        />
      );
    }
    // Without portfolios yet, the table would say the org has none.
    if (!organizationRecordsQuery.data || !projects) {
      return <CenteredSpinner />;
    }
    return (
      <PortfolioPurchasesTable
        portfolios={projectList}
        billingRecords={organizationRecordsQuery.data}
        heading={portfolioSelect}
      />
    );
  };

  return (
    <GlobalSettings.Body title="Plan and Billing">
      <>
        <PlanBillingCard
          cancelSubscription={cancelSubscription}
          accessSubscriptionMaybe={accessSubscriptionMaybe}
          organization={organization}
          profile={profile}
          hasEnded={hasEnded}
          currentLensTier={currentLensTier}
          contractsMaybe={contractsMaybe}
          organizationUsers={organizationUsers}
          projects={projects}
          isLoadingContracts={contractsMaybe.status === 'unknown'}
          imagerySubscriptionIds={allImagerySubscriptionIds}
        />
        {showImageryPrepaid && (
          <ImageryPrepaidSection
            organizationId={organizationId}
            canAddPrepaid={isOwnerOrAdmin(profile.get('role'))}
          />
        )}
        <h2>Order History</h2>

        {orgPaysAtCheckout ? (
          <>
            <div className={cs.tableSelection}>
              {checkoutTabs.map(({id, label}) => (
                <B.AnchorButton
                  key={id}
                  minimal
                  className={cs.tableButtons}
                  intent="primary"
                  active={checkoutTab === id}
                  onClick={() => showCheckoutTab(id)}
                >
                  {label}
                </B.AnchorButton>
              ))}
            </div>
            {renderCheckoutView()}
          </>
        ) : (
          <>
            <div className={cs.tableSelection}>
              <B.AnchorButton
                minimal
                className={cs.tableButtons}
                intent="primary"
                active={!selectedProject}
                onClick={() => setInvoicedProject(null)}
              >
                Overview
              </B.AnchorButton>
              <B.Popover
                minimal
                isOpen={projectSelectOpen}
                onClose={() => setProjectSelectOpen(false)}
                disabled={projectList.length < 1}
                usePortal={false}
                content={
                  <B.Menu>
                    {projectList.map((projectSelection) => (
                      <B.MenuItem
                        key={projectSelection.id}
                        text={projectSelection.name}
                        icon={selectedProject?.id === projectSelection.id ? 'small-tick' : 'blank'}
                        onClick={() => {
                          setInvoicedProject(projectSelection);
                        }}
                      />
                    ))}
                  </B.Menu>
                }
              >
                <B.AnchorButton
                  minimal
                  className={cs.tableButtons}
                  intent="primary"
                  active={!!selectedProject}
                  onClick={() => setProjectSelectOpen(true)}
                  disabled={projectSelectionDisabled}
                >
                  Order History by Portfolio{selectedProject ? `: ${selectedProject.name} ` : ' '}
                  <span className={cs.caretSpace}>
                    <B.Icon icon={'caret-down'} />
                  </span>
                </B.AnchorButton>
              </B.Popover>
            </div>
            {selectedProject ? (
              <ContractTable
                organizationId={organizationId}
                currentImageryContract={currentImageryContract}
                allImageryContracts={allImageryContracts}
                billingRecords={imageryPurchaseBillingRecords}
                featuresById={featuresById}
              />
            ) : (
              contractHistory
            )}
          </>
        )}
      </>
    </GlobalSettings.Body>
  );
};

// Thin wrapper around the Imagery Contract Table, requiring mostly identical props, and returning
// a spinner if any of its props are not yet available, e.g. between portfolio selection and load.
const ContractTable: React.FunctionComponent<
  React.PropsWithChildren<{
    organizationId: string;
    // Null lists every purchase unfiltered, as for an org that pays at checkout.
    currentImageryContract: StatusMaybe<ApiImageryContract | null> | null;
    allImageryContracts: ApiImageryContractWithProjects[] | null;
    billingRecords: ApiImageryPurchaseBillingRecord[] | null;
    featuresById: I.Map<number, I.ImmutableOf<ApiFeature>> | null;
    heading?: React.ReactNode;
    actions?: React.ReactNode;
  }>
> = ({
  organizationId,
  currentImageryContract,
  allImageryContracts,
  billingRecords,
  featuresById,
  heading,
  actions,
}) => {
  if (currentImageryContract?.status === 'some' && currentImageryContract.value == null) {
    return (
      <B.NonIdealState
        icon="document"
        description="This portfolio has no active imagery contract."
      />
    );
  }

  const contract = currentImageryContract?.value ?? null;
  if (
    (currentImageryContract !== null && (!contract || !allImageryContracts)) ||
    !billingRecords ||
    !featuresById
  ) {
    return <CenteredSpinner />;
  }

  return (
    <ImageryContractTable
      organizationId={organizationId}
      imageryContracts={allImageryContracts ?? []}
      currentImageryContract={contract}
      billingRecordsForProject={billingRecords}
      featuresById={featuresById}
      heading={heading}
      actions={actions}
    />
  );
};

export default BillingView;
